import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Plus, HelpCircle, Clock, CheckCircle2, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { RoadSign, ROAD_SIGN_CODES } from '../../components/RoadSign';

const THEMES = [
  { code: 'R', name: 'La route' },
  { code: 'C', name: 'La circulation' },
  { code: 'L', name: 'Le conducteur' },
  { code: 'U', name: 'Les autres usagers' },
  { code: 'D', name: 'Notions diverses' },
  { code: 'M', name: 'Mécanique' },
  { code: 'P', name: 'Équipements' },
  { code: 'E', name: 'Environnement' },
  { code: 'S', name: 'Premiers secours' },
];

export default function CommunityQuestions() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    theme_code: 'R',
    question: '',
    choices: ['', '', '', ''],
    correct_answer: 'A',
    explanation: '',
    sign_code: '',
    source: '',
  });
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.listCommunityQuestions(); setList(r.questions || []); }
    catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.question.trim().length < 10) return toast.error('Question trop courte');
    const validChoices = form.choices.filter(c => c.trim());
    if (validChoices.length < 2) return toast.error('Au moins 2 choix requis');
    setPosting(true);
    try {
      await api.proposeCommunityQuestion({ ...form, choices: validChoices });
      toast.success('Question soumise pour validation admin');
      setShowForm(false);
      setForm({ theme_code: 'R', question: '', choices: ['', '', '', ''], correct_answer: 'A', explanation: '', sign_code: '', source: '' });
      load();
    } catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setPosting(false); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="space-y-5" data-testid="community-questions-page">
      <Button onClick={() => navigate('/reseau')} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour au réseau
      </Button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-2">
            <HelpCircle className="w-7 h-7 text-[hsl(var(--primary))]" /> QCM communautaires
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Proposez des nouvelles questions ETM. Une fois validées, elles enrichissent la base partagée.</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="mc-btn-primary" data-testid="new-question-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Proposer une question
        </Button>
      </div>

      {showForm && (
        <div className="mc-card p-5 space-y-3" data-testid="new-question-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.theme_code} onChange={e => setForm({ ...form, theme_code: e.target.value })} className="px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="q-theme">
              {THEMES.map(t => <option key={t.code} value={t.code}>{t.code} — {t.name}</option>)}
            </select>
            <select value={form.sign_code} onChange={e => setForm({ ...form, sign_code: e.target.value })} className="px-3 py-2 rounded-lg border border-border bg-background text-sm" data-testid="q-sign">
              <option value="">— Aucun panneau —</option>
              {ROAD_SIGN_CODES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {form.sign_code && (
            <div className="flex items-center justify-center p-3 rounded-lg bg-muted/30">
              <RoadSign code={form.sign_code} size={64} />
            </div>
          )}
          <textarea placeholder="Énoncé de la question" value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold" data-testid="q-question" />
          <div className="grid sm:grid-cols-2 gap-2">
            {form.choices.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 cursor-pointer ${form.correct_answer === String.fromCharCode(65 + i) ? 'bg-[hsl(var(--success))] text-white' : 'bg-muted'}`} onClick={() => setForm({ ...form, correct_answer: String.fromCharCode(65 + i) })}>
                  {String.fromCharCode(65 + i)}
                </span>
                <input type="text" placeholder={`Choix ${String.fromCharCode(65 + i)}`} value={c} onChange={e => { const nc = [...form.choices]; nc[i] = e.target.value; setForm({ ...form, choices: nc }); }} className="flex-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm" data-testid={`q-choice-${i}`} />
              </div>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground">Cliquez sur la lettre A/B/C/D pour désigner la bonne réponse</div>
          <textarea placeholder="Explication (recommandé)" value={form.explanation} onChange={e => setForm({ ...form, explanation: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="q-explanation" />
          <input type="text" placeholder="Source (article du Code, etc. - optionnel)" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm">Annuler</Button>
            <Button onClick={submit} disabled={posting} className="mc-btn-primary" size="sm" data-testid="q-submit">{posting ? 'Envoi…' : 'Soumettre pour validation'}</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mc-card p-10 text-center text-muted-foreground">Chargement…</div>
      ) : list.length === 0 ? (
        <div className="mc-card p-10 text-center text-sm text-muted-foreground" data-testid="questions-empty">Aucune question communautaire publiée pour le moment.</div>
      ) : (
        <div className="space-y-2">
          {list.map(q => (
            <div key={q.id} className="mc-card p-4" data-testid={`community-q-${q.id}`}>
              <div className="flex items-start gap-3">
                {q.sign_code && <RoadSign code={q.sign_code} size={48} />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">Thème {q.theme_code}</span>
                    {q.status === 'pending' && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#f59e0b]"><Clock className="w-3 h-3" /> En attente</span>}
                    {q.status === 'approved' && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#16a34a]"><CheckCircle2 className="w-3 h-3" /> Intégrée à la base</span>}
                    {q.status === 'rejected' && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground"><X className="w-3 h-3" /> Rejetée</span>}
                  </div>
                  <p className="text-sm font-semibold">{q.question}</p>
                  <div className="text-[10px] text-muted-foreground mt-2 inline-flex items-center gap-1">
                    <User className="w-3 h-3" /> {q.author_name} · {fmtDate(q.created_at)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
